// Tela "Uso da API" (/messages-api) no padrão Aeros Dark — mockup
// "Tela Aeros Dark - API de Mensagens".
//
// ATENÇÃO (docs/CONTEXT.md + CLAUDE.md): esta tela É a documentação pública
// de POST /api/messages/send. Campo novo no contrato precisa entrar em DOIS
// lugares aqui — a lista `textBodyLines` (i18n `messagesAPIInstructions.*`) e
// TODOS os exemplos (`textCopyExample`, `textExamples`, `mediaExamples`), com
// valor realista em cada linguagem, nunca só um comentário.
import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";

import {
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
} from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import SendIcon from "@material-ui/icons/Send";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import BlockIcon from "@material-ui/icons/Block";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import ImageIcon from "@material-ui/icons/Image";
import VideocamIcon from "@material-ui/icons/Videocam";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { Field, Form, Formik } from "formik";
import { toast } from "react-toastify";
import axios from "axios";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import useApiPageStyles from "./apiPageStyles";
import { tempoRelativo } from "./apiResultRules";
import TokensModal from "./TokensModal";
import { buildSmsExamples } from "./smsExamples";
import { buildStatusExamples } from "./statusExamples";
import ResultDrawer from "./ResultDrawer";
import { AEROS } from "../../styles/aerosTokens";

// O backend exige X-Origin em POST /api/messages/send (400 sem ele); o
// formulário de teste da própria tela se identifica com um valor fixo, que
// aparece assim no log de auditoria.
const ORIGEM_TESTE_TELA = "Pulse (teste da tela)";

// Renderiza uma linha `chave: valor` colorindo a chave como em um editor.
const CodeLine = ({ text, indent = 1, classes }) => {
  const idx = text.indexOf(":");
  const pad = "  ".repeat(indent);
  if (idx === -1) {
    return (
      <div className={classes.codeLine}>
        {pad}
        {text}
      </div>
    );
  }
  return (
    <div className={classes.codeLine}>
      {pad}
      <span className={classes.codeKey}>{text.slice(0, idx)}</span>
      {text.slice(idx)}
    </div>
  );
};

// Ícone e tom do arquivo escolhido, pelo mimetype — o mockup usa o ladrilho
// vermelho de PDF e o ciano de imagem/vídeo.
const fileVisual = (mimetype = "") => {
  if (mimetype.includes("pdf")) return { Icon: PictureAsPdfIcon, tone: "pdf" };
  if (mimetype.startsWith("image/")) return { Icon: ImageIcon, tone: "image" };
  if (mimetype.startsWith("video/")) return { Icon: VideocamIcon, tone: "image" };
  return { Icon: InsertDriveFileOutlinedIcon, tone: "other" };
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MessagesAPI = () => {
  const classes = useApiPageStyles();
  const c = useCampaignPageStyles({ grid: "1fr" });
  const history = useHistory();

  const [formMessageTextData] = useState({
    token: "",
    number: "",
    body: "",
    userId: "",
    queueId: "",
    sendSignature: false,
    closeTicket: false,
  });
  const [formMessageMediaData] = useState({
    token: "",
    number: "",
    medias: "",
    body: "",
    userId: "",
    queueId: "",
    sendSignature: false,
    closeTicket: false,
  });
  const [file, setFile] = useState({});
  const { user } = useContext(AuthContext);

  // Seletor de conexão: preenche o token sozinho e, em conexão oficial,
  // libera a escolha de template a enviar (vale dentro e fora da janela de 24h).
  const [connections, setConnections] = useState([]);
  const [selectedConnId, setSelectedConnId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParams, setTemplateParams] = useState([]);
  // URL pública da mídia do cabeçalho, exigida quando o template selecionado
  // tem HEADER de IMAGE/VIDEO/DOCUMENT (headerMidia preenchido).
  const [templateHeaderMediaUrl, setTemplateHeaderMediaUrl] = useState("");

  // Aba de linguagem dos exemplos: compartilhada entre os dois painéis para a
  // pessoa não precisar escolher duas vezes.
  const [exampleLang, setExampleLang] = useState("fields");

  const [tokensOpen, setTokensOpen] = useState(false);
  const [showToken, setShowToken] = useState(false);
  // Resultado do último "Testar envio": abre o drawer com a resposta real.
  const [result, setResult] = useState(null);
  // Resumo só do card de status ("Último teste: 200 OK · há 4 min"). Vive na
  // sessão; não há histórico de testes no backend.
  const [lastTest, setLastTest] = useState(null);
  const [usage, setUsage] = useState(null);
  const [smsToken, setSmsToken] = useState(null);
  const isAdmin = user?.profile === "admin";

  const selectedConn = connections.find((conn) => conn.id === selectedConnId) || null;
  const ehOficial = selectedConn?.channel === "whatsapp_oficial";
  // Com template selecionado o corpo é ignorado pela API — o campo Mensagem
  // vira um aviso em vez de aceitar texto que não vai a lugar nenhum.
  const corpoIgnorado = ehOficial && !!selectedTemplate;

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useExternalApi) {
        toast.error(i18n.t("aeros.api.noPermission"));
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function fetchConnections() {
      try {
        const { data } = await api.get("/whatsapp");
        setConnections(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    }
    fetchConnections();
  }, []);

  // KPI de envios pela API nos últimos 30 dias. Falha silenciosa de
  // propósito: o card some, mas a documentação — o que a tela existe para
  // mostrar — continua de pé.
  useEffect(() => {
    async function fetchUsage() {
      try {
        const { data } = await api.get("/api-usage/resumo");
        setUsage(data);
      } catch (err) {
        setUsage(null);
      }
    }
    fetchUsage();
  }, []);

  // Token de SMS da empresa: só o admin lê (os demais recebem 403), então
  // nem pergunta para quem não é admin.
  useEffect(() => {
    if (!isAdmin) return;
    async function fetchSmsToken() {
      try {
        const { data } = await api.get("/sms/api-token");
        setSmsToken(data?.token || null);
      } catch (err) {
        toastError(err);
      }
    }
    fetchSmsToken();
  }, [isAdmin]);

  const handleGenerateSmsToken = useCallback(async () => {
    try {
      const { data } = await api.post("/sms/api-token/regenerate");
      setSmsToken(data?.token || null);
      toast.success(i18n.t("aeros.api.tokens.sms.generated"));
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    async function fetchTemplates() {
      setTemplates([]);
      setSelectedTemplate(null);
      setTemplateParams([]);
      setTemplateHeaderMediaUrl("");
      if (!ehOficial || !selectedConnId) return;
      try {
        // incluirMidiaHeader=1: aqui a URL da mídia do cabeçalho é coletada
        // (templateHeaderMediaUrl), então templates com mídia também servem.
        const { data } = await api.get(
          `/whatsapp/${selectedConnId}/templates?incluirMidiaHeader=1`
        );
        setTemplates(data?.templates || []);
      } catch (err) {
        toastError(err);
      }
    }
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnId]);

  const handleSelectTemplate = (shortcode) => {
    const tmpl = templates.find((t) => t.shortcode === shortcode) || null;
    setSelectedTemplate(tmpl);
    setTemplateParams(tmpl ? Array(tmpl.variaveis || 0).fill("") : []);
    setTemplateHeaderMediaUrl("");
  };

  const getEndpoint = () => {
    return process.env.REACT_APP_BACKEND_URL + "/api/messages/send";
  };

  const handleCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(i18n.t("aeros.api.copied"));
    } catch (err) {
      toastError(err);
    }
  }, []);

  // Guarda o resultado do teste com a latência medida aqui: a API não devolve
  // tempo de processamento, e o número do mockup só faz sentido como o que a
  // pessoa esperou de fato.
  const registraResultado = (ok, status, statusText, body, iniciadoEm) => {
    const ms = Math.round(performance.now() - iniciadoEm);
    setResult({
      ok,
      status,
      statusText,
      ms,
      body,
      connectionName: selectedConn?.name,
    });
    setLastTest({ ok, status, statusText, at: Date.now() });
  };

  const enviaTeste = async (config, iniciadoEm) => {
    try {
      const resposta = await axios.request(config);
      registraResultado(true, resposta.status, resposta.statusText, resposta.data, iniciadoEm);
      return true;
    } catch (err) {
      // Sem `err.response` a requisição nem chegou ao servidor (rede, CORS,
      // backend fora) — o drawer trata isso como "sem resposta", não como 4xx.
      registraResultado(
        false,
        err.response?.status,
        err.response?.statusText,
        err.response?.data ?? err.message,
        iniciadoEm
      );
      return false;
    }
  };

  const handleSendTextMessage = async (values) => {
    const { number, body, userId, queueId, sendSignature, closeTicket } = values;
    const data = { number, userId, queueId, sendSignature, closeTicket };
    if (ehOficial && selectedTemplate) {
      // Com template o backend descarta o body: nao exigir nem enviar.
      data.templateName = selectedTemplate.shortcode;
      data.templateParams = templateParams;
      if (selectedTemplate.headerMidia) {
        data.templateHeaderMediaUrl = templateHeaderMediaUrl;
      }
    } else {
      data.body = body;
    }
    return enviaTeste(
      {
        url: getEndpoint(),
        method: "POST",
        data,
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${values.token}`,
          "X-Origin": ORIGEM_TESTE_TELA,
        },
      },
      performance.now()
    );
  };

  const handleSendMediaMessage = async (values) => {
    const firstFile = file[0];
    if (!firstFile) {
      toast.error(i18n.t("aeros.api.fileRequired"));
      return false;
    }
    const data = new FormData();
    data.append("number", values.number);
    data.append("body", values.body ? values.body : firstFile.name);
    data.append("userId", values.userId);
    data.append("queueId", values.queueId);
    data.append("sendSignature", values.sendSignature);
    data.append("closeTicket", values.closeTicket);
    data.append("medias", firstFile);
    return enviaTeste(
      {
        url: getEndpoint(),
        method: "POST",
        data,
        headers: {
          "Content-type": "multipart/form-data",
          Authorization: `Bearer ${values.token}`,
          "X-Origin": ORIGEM_TESTE_TELA,
        },
      },
      performance.now()
    );
  };

  const textBodyLines = [
    i18n.t("messagesAPIInstructions.fieldNumber"),
    i18n.t("messagesAPIInstructions.fieldBody"),
    i18n.t("messagesAPIInstructions.fieldUserId"),
    i18n.t("messagesAPIInstructions.fieldQueueId"),
    i18n.t("messagesAPIInstructions.fieldSendSignature"),
    i18n.t("messagesAPIInstructions.fieldCloseTicket"),
    i18n.t("messagesAPIInstructions.fieldTemplateName"),
    i18n.t("messagesAPIInstructions.fieldTemplateParams"),
    i18n.t("messagesAPIInstructions.fieldTemplateHeaderMediaUrl"),
    i18n.t("messagesAPIInstructions.fieldTemplateOrderDetails"),
    i18n.t("messagesAPIInstructions.fieldTemplateOrderDetailsExpiration"),
  ];

  const mediaBodyLines = [
    'number: "558599999999"',
    'body: "Mensagem"',
    'userId: ID do usuário ou ""',
    'queueId: ID da fila ou ""',
    "medias: arquivo",
    `sendSignature: ${i18n.t("messagesAPIInstructions.singMessage")}`,
    `closeTicket: ${i18n.t("messagesAPIInstructions.closeTicket")}`,
  ];

  const textCopyExample = [
    `POST ${getEndpoint()}`,
    "Authorization: Bearer SEU_TOKEN",
    "X-Origin: ERP Financeiro",
    "Content-Type: application/json",
    "",
    "{",
    '  "number": "558599999999",',
    '  "body": "Mensagem",',
    '  "userId": "",',
    '  "queueId": "",',
    '  "sendSignature": true,',
    '  "closeTicket": false,',
    '  "templateName": "nome_do_template",',
    '  "templateParams": ["{nome}", "Valor da variavel 2"],',
    '  "templateHeaderMediaUrl": "https://exemplo.com/arquivo.png",',
    '  "templateOrderDetails": {',
    '    "reference_id": "cob-2026-000123",',
    '    "type": "digital-goods",',
    '    "payment_type": "br",',
    '    "payment_settings": [{',
    '      "type": "pix_dynamic_code",',
    '      "pix_dynamic_code": {',
    '        "code": "00020101021226...6304ABCD",',
    '        "merchant_name": "EMPRESA LTDA",',
    '        "key": "39580525000189",',
    '        "key_type": "CNPJ"',
    "      }",
    "    }],",
    '    "currency": "BRL",',
    '    "total_amount": { "value": 10000, "offset": 100 },',
    '    "order": {',
    '      "status": "pending",',
    '      "expiration": { "timestamp": "1790000000", "description": "Pix válido por 30 minutos" },',
    '      "items": [{ "retailer_id": "srv-001", "name": "Serviço", "quantity": 1, "amount": { "value": 10000, "offset": 100 } }],',
    '      "subtotal": { "value": 10000, "offset": 100 },',
    '      "tax": { "value": 0, "offset": 100 }',
    "    }",
    "  }",
    "}",
    "",
    "# 200 (conexão oficial) => aceita pela Meta; a entrega é assíncrona:",
    '# { "message": "Mensagem enviada com sucesso", "companyId": 1, "ticketId": 215,',
    '#   "messageId": 4821, "wid": "wamid.HBgMNTU4NTk5OTk5OTk5FQIAERgS", "status": "accepted" }',
    `# Consulte o resultado em GET ${getEndpoint().replace(/\/send$/, "/4821/status")}`,
    '# Com problema de conta na conexão vem também "warning": { code, category, reason, action, link, reasonOriginal }.',
    "# Conexão QR Code (Baileys): sem messageId (a mensagem nasce depois, pela fila).",
  ].join("\n");

  const endpoint = getEndpoint();
  // A API de SMS mora no mesmo backend: mesma origem de getEndpoint().
  const smsExamples = buildSmsExamples(endpoint.replace(/\/api\/messages\/send$/, ""));
  // A consulta de status também mora em /api/messages (:id = messageId do envio).
  const baseUrl = endpoint.replace(/\/api\/messages\/send$/, "");
  const statusExamples = buildStatusExamples(baseUrl);

  const textExamples = {
    curl: `RESP=$(curl -s -X POST "${endpoint}" \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -H "X-Origin: ERP Financeiro" \\
  -H "Content-Type: application/json" \\
  -d '{
    "number": "558599999999",
    "body": "Mensagem",
    "userId": "",
    "queueId": "",
    "sendSignature": true,
    "closeTicket": false,
    "templateName": "nome_do_template",
    "templateParams": ["{nome}", "Valor da variavel 2"],
    "templateHeaderMediaUrl": "https://exemplo.com/arquivo.png",
    "templateOrderDetails": {
      "reference_id": "cob-2026-000123",
      "type": "digital-goods",
      "payment_type": "br",
      "payment_settings": [{
        "type": "pix_dynamic_code",
        "pix_dynamic_code": {
          "code": "00020101021226...6304ABCD",
          "merchant_name": "EMPRESA LTDA",
          "key": "39580525000189",
          "key_type": "CNPJ"
        }
      }],
      "currency": "BRL",
      "total_amount": { "value": 10000, "offset": 100 },
      "order": {
        "status": "pending",
        "expiration": { "timestamp": "1790000000", "description": "Pix válido por 30 minutos" },
        "items": [{ "retailer_id": "srv-001", "name": "Serviço", "quantity": 1, "amount": { "value": 10000, "offset": 100 } }],
        "subtotal": { "value": 10000, "offset": 100 },
        "tax": { "value": 0, "offset": 100 }
      }
    }
  }')
# templateName/templateParams: opcionais, apenas conexão oficial. Com
# templateName o TEMPLATE é enviado (dentro ou fora da janela de 24h) e o
# body é descartado; sem ele, texto livre na janela aberta e template
# automático na fechada. Omita nos demais casos
# templateHeaderMediaUrl: obrigatório só quando o template tem mídia
# no cabeçalho (IMAGE/VIDEO/DOCUMENT); URL http(s) pública do arquivo
# templateOrderDetails: opcional, só com templateName — botão Pix do
# template de cobrança ORDER_DETAILS. O code é o copia-e-cola dinâmico;
# valores em centavos ({ value, offset: 100 }; R$ 100,00 => 10000);
# reference_id único por cobrança; expiration ao menos 5 min no futuro.
# A expiration expira só o botão no card — o code tem validade própria
# no PSP: derive o timestamp da validade real da cobrança.
# Erro de formato responde 422 com a regra violada.
#
# 200 (conexão oficial) => aceita pela Meta; a entrega é assíncrona:
# { "message": "Mensagem enviada com sucesso", "companyId": 1, "ticketId": 215,
#   "messageId": 4821, "wid": "wamid.HBgMNTU4NTk5OTk5OTk5FQIAERgS", "status": "accepted" }
# Se a conexão tem problema de conta, vem também "warning": { code, category,
# reason, action, link, reasonOriginal }. Conexão QR Code (Baileys): sem
# messageId. Se a Meta recusar na hora, o erro HTTP traz o motivo em "error".
# messageId e wid podem vir null (aceita, mas sem registro consultável ainda):
# nesse caso não há status a consultar. Com messageId, consulte o resultado:
MESSAGE_ID=$(echo "$RESP" | jq -r '.messageId // empty')
[ -n "$MESSAGE_ID" ] && curl -s "${baseUrl}/api/messages/$MESSAGE_ID/status" \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -H "X-Origin: ERP Financeiro"`,
    node: `const axios = require("axios");

(async () => {
  const { data } = await axios.post(
    "${endpoint}",
    {
      number: "558599999999",
      body: "Mensagem",
      userId: "",
      queueId: "",
      sendSignature: true, // assinar a mensagem com o nome do atendente
      closeTicket: false, // true encerra o ticket após o envio
      // Opcionais, apenas conexão oficial: com templateName o TEMPLATE é
      // enviado (dentro ou fora da janela de 24h) e o body é descartado.
      // Params na ordem das variáveis; aceita {nome}, {numero}, {email}.
      templateName: "nome_do_template",
      templateParams: ["{nome}", "Valor da variavel 2"],
      // Obrigatório só quando o template tem mídia no cabeçalho:
      templateHeaderMediaUrl: "https://exemplo.com/arquivo.png",
      // Opcional, só com templateName: botão Pix via template de cobrança
      // ORDER_DETAILS. code é o copia-e-cola dinâmico; valores em centavos
      // ({ value, offset: 100 }; R$ 100,00 => 10000). Erro => 422.
      templateOrderDetails: {
        reference_id: "cob-2026-000123",
        type: "digital-goods",
        payment_type: "br",
        payment_settings: [{
          type: "pix_dynamic_code",
          pix_dynamic_code: {
            code: "00020101021226...6304ABCD",
            merchant_name: "EMPRESA LTDA",
            key: "39580525000189",
            key_type: "CNPJ",
          },
        }],
        currency: "BRL",
        total_amount: { value: 10000, offset: 100 },
        order: {
          status: "pending",
          // Opcional; epoch em segundos, >= 5 min no futuro. Expira só o
          // botão no card: alinhe à validade real do Pix gerado no PSP.
          expiration: { timestamp: "1790000000", description: "Pix válido por 30 minutos" },
          items: [{ retailer_id: "srv-001", name: "Serviço", quantity: 1, amount: { value: 10000, offset: 100 } }],
          subtotal: { value: 10000, offset: 100 },
          tax: { value: 0, offset: 100 },
        },
      },
    },
    { headers: { Authorization: "Bearer SEU_TOKEN", "X-Origin": "ERP Financeiro" } }
  );

  // 200 = aceita pela Meta (data.status === "accepted"); a entrega é assíncrona.
  // Conexão QR Code (Baileys): data não traz messageId nem wid.
  // data.warning só aparece se a conexão tem problema de conta.
  if (data.warning) console.warn(data.warning.reason, data.warning.link);

  // messageId pode ser null (aceita, mas sem registro consultável ainda): nesse
  // caso não há status a consultar. Com messageId, consulte o resultado:
  if (data.messageId) {
    const { data: situacao } = await axios.get(
      "${baseUrl}/api/messages/" + data.messageId + "/status",
      { headers: { Authorization: "Bearer SEU_TOKEN", "X-Origin": "ERP Financeiro" } }
    );
    // situacao.status: "accepted" | "sent" | "delivered" | "read" | "failed"
  }
})();`,
    ts: `const resposta = await fetch("${endpoint}", {
  method: "POST",
  headers: {
    Authorization: "Bearer SEU_TOKEN",
    "X-Origin": "ERP Financeiro",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    number: "558599999999",
    body: "Mensagem",
    userId: "",
    queueId: "",
    sendSignature: true, // assinar a mensagem com o nome do atendente
    closeTicket: false, // true encerra o ticket após o envio
    // Opcionais, apenas conexão oficial: com templateName o TEMPLATE é
    // enviado (dentro ou fora da janela de 24h) e o body é descartado.
    // Params na ordem das variáveis; aceita {nome}, {numero}, {email}.
    templateName: "nome_do_template",
    templateParams: ["{nome}", "Valor da variavel 2"],
    // Obrigatório só quando o template tem mídia no cabeçalho:
    templateHeaderMediaUrl: "https://exemplo.com/arquivo.png",
    // Opcional, só com templateName: botão Pix via template de cobrança
    // ORDER_DETAILS. code é o copia-e-cola dinâmico; valores em centavos
    // ({ value, offset: 100 }; R$ 100,00 => 10000). Erro => 422.
    templateOrderDetails: {
      reference_id: "cob-2026-000123",
      type: "digital-goods",
      payment_type: "br",
      payment_settings: [{
        type: "pix_dynamic_code",
        pix_dynamic_code: {
          code: "00020101021226...6304ABCD",
          merchant_name: "EMPRESA LTDA",
          key: "39580525000189",
          key_type: "CNPJ",
        },
      }],
      currency: "BRL",
      total_amount: { value: 10000, offset: 100 },
      order: {
        status: "pending",
        // Opcional; epoch em segundos, >= 5 min no futuro. Expira só o
        // botão no card: alinhe à validade real do Pix gerado no PSP.
        expiration: { timestamp: "1790000000", description: "Pix válido por 30 minutos" },
        items: [{ retailer_id: "srv-001", name: "Serviço", quantity: 1, amount: { value: 10000, offset: 100 } }],
        subtotal: { value: 10000, offset: 100 },
        tax: { value: 0, offset: 100 },
      },
    },
  }),
});

// 200 = aceita pela Meta; a entrega é assíncrona. Na conexão QR Code
// (Baileys) não há messageId/wid; warning só aparece com problema de conta.
const dados: {
  message: string;
  companyId: number;
  ticketId: number;
  messageId?: number | null; // null = aceita, sem registro consultável
  wid?: string | null;
  status?: "accepted";
  warning?: {
    code: number | null;
    category: string;
    reason: string;
    action: string;
    link: string | null;
    reasonOriginal: string | null;
  };
} = await resposta.json();

// messageId pode ser null (aceita, mas sem registro consultável ainda): nesse
// caso não há status a consultar. Com messageId, consulte o resultado:
if (dados.messageId) {
  const consulta = await fetch("${baseUrl}/api/messages/" + dados.messageId + "/status", {
    headers: { Authorization: "Bearer SEU_TOKEN", "X-Origin": "ERP Financeiro" },
  });
  const situacao: { status: "accepted" | "sent" | "delivered" | "read" | "failed" } =
    await consulta.json();
}`,
    php: `<?php
$ch = curl_init("${endpoint}");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "Authorization: Bearer SEU_TOKEN",
    "X-Origin: ERP Financeiro",
    "Content-Type: application/json",
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    "number"        => "558599999999",
    "body"          => "Mensagem",
    "userId"        => "",
    "queueId"       => "",
    "sendSignature" => true,  // assinar a mensagem com o nome do atendente
    "closeTicket"   => false, // true encerra o ticket após o envio
    // Opcionais, apenas conexão oficial: com templateName o TEMPLATE é
    // enviado (dentro ou fora da janela de 24h) e o body é descartado.
    // Params na ordem das variáveis; aceita {nome}, {numero}, {email}.
    "templateName"   => "nome_do_template",
    "templateParams" => ["{nome}", "Valor da variavel 2"],
    // Obrigatório só quando o template tem mídia no cabeçalho:
    "templateHeaderMediaUrl" => "https://exemplo.com/arquivo.png",
    // Opcional, só com templateName: botão Pix via template de cobrança
    // ORDER_DETAILS. code é o copia-e-cola dinâmico; valores em centavos
    // (value/offset 100; R$ 100,00 => 10000). Erro => 422.
    "templateOrderDetails" => [
        "reference_id"     => "cob-2026-000123",
        "type"             => "digital-goods",
        "payment_type"     => "br",
        "payment_settings" => [[
            "type" => "pix_dynamic_code",
            "pix_dynamic_code" => [
                "code"          => "00020101021226...6304ABCD",
                "merchant_name" => "EMPRESA LTDA",
                "key"           => "39580525000189",
                "key_type"      => "CNPJ",
            ],
        ]],
        "currency"     => "BRL",
        "total_amount" => ["value" => 10000, "offset" => 100],
        "order" => [
            "status"     => "pending",
            // Opcional; epoch em segundos, >= 5 min no futuro. Expira só o
            // botão no card: alinhe à validade real do Pix gerado no PSP.
            "expiration" => ["timestamp" => "1790000000", "description" => "Pix válido por 30 minutos"],
            "items"      => [[
                "retailer_id" => "srv-001",
                "name"        => "Serviço",
                "quantity"    => 1,
                "amount"      => ["value" => 10000, "offset" => 100],
            ]],
            "subtotal" => ["value" => 10000, "offset" => 100],
            "tax"      => ["value" => 0, "offset" => 100],
        ],
    ],
]));
$resposta = json_decode(curl_exec($ch), true);
curl_close($ch);

// 200 = aceita pela Meta ($resposta["status"] === "accepted"); a entrega é
// assíncrona. Conexão QR Code (Baileys): sem messageId/wid.
if (isset($resposta["warning"])) {
    error_log($resposta["warning"]["reason"] . " " . $resposta["warning"]["link"]);
}

// messageId pode ser null (aceita, mas sem registro consultável ainda): nesse
// caso não há status a consultar. Com messageId, consulte o resultado:
if (!empty($resposta["messageId"])) {
    $ch = curl_init("${baseUrl}/api/messages/" . $resposta["messageId"] . "/status");
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_HTTPHEADER, [
        "Authorization: Bearer SEU_TOKEN",
        "X-Origin: ERP Financeiro",
    ]);
    $situacao = json_decode(curl_exec($ch), true);
    curl_close($ch);
    // $situacao["status"]: accepted | sent | delivered | read | failed
}`,
    python: `import requests

resposta = requests.post(
    "${endpoint}",
    json={
        "number": "558599999999",
        "body": "Mensagem",
        "userId": "",
        "queueId": "",
        "sendSignature": True,  # assinar a mensagem com o nome do atendente
        "closeTicket": False,  # True encerra o ticket após o envio
        # Opcionais, apenas conexão oficial: com templateName o TEMPLATE é
        # enviado (dentro ou fora da janela de 24h) e o body é descartado.
        # Params na ordem das variáveis; aceita {nome}, {numero}, {email}.
        "templateName": "nome_do_template",
        "templateParams": ["{nome}", "Valor da variavel 2"],
        # Obrigatório só quando o template tem mídia no cabeçalho:
        "templateHeaderMediaUrl": "https://exemplo.com/arquivo.png",
        # Opcional, só com templateName: botão Pix via template de cobrança
        # ORDER_DETAILS. code é o copia-e-cola dinâmico; valores em centavos
        # ({"value": ..., "offset": 100}; R$ 100,00 => 10000). Erro => 422.
        "templateOrderDetails": {
            "reference_id": "cob-2026-000123",
            "type": "digital-goods",
            "payment_type": "br",
            "payment_settings": [{
                "type": "pix_dynamic_code",
                "pix_dynamic_code": {
                    "code": "00020101021226...6304ABCD",
                    "merchant_name": "EMPRESA LTDA",
                    "key": "39580525000189",
                    "key_type": "CNPJ",
                },
            }],
            "currency": "BRL",
            "total_amount": {"value": 10000, "offset": 100},
            "order": {
                "status": "pending",
                # Opcional; epoch em segundos, >= 5 min no futuro. Expira só o
                # botão no card: alinhe à validade real do Pix gerado no PSP.
                "expiration": {"timestamp": "1790000000", "description": "Pix válido por 30 minutos"},
                "items": [{
                    "retailer_id": "srv-001",
                    "name": "Serviço",
                    "quantity": 1,
                    "amount": {"value": 10000, "offset": 100},
                }],
                "subtotal": {"value": 10000, "offset": 100},
                "tax": {"value": 0, "offset": 100},
            },
        },
    },
    headers={"Authorization": "Bearer SEU_TOKEN", "X-Origin": "ERP Financeiro"},
)
dados = resposta.json()

# 200 = aceita pela Meta (dados["status"] == "accepted"); a entrega é
# assíncrona. Conexão QR Code (Baileys): sem messageId/wid.
if "warning" in dados:
    print(dados["warning"]["reason"], dados["warning"]["link"])

# messageId pode ser None (aceita, mas sem registro consultável ainda): nesse
# caso não há status a consultar. Com messageId, consulte o resultado:
if dados.get("messageId"):
    situacao = requests.get(
        f"${baseUrl}/api/messages/{dados['messageId']}/status",
        headers={"Authorization": "Bearer SEU_TOKEN", "X-Origin": "ERP Financeiro"},
    ).json()
    # situacao["status"]: accepted | sent | delivered | read | failed`,
  };

  const mediaExamples = {
    curl: `RESP=$(curl -s -X POST "${endpoint}" \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -H "X-Origin: ERP Financeiro" \\
  -F "number=558599999999" \\
  -F "body=Legenda do arquivo" \\
  -F "userId=" \\
  -F "queueId=" \\
  -F "sendSignature=true" \\
  -F "closeTicket=false" \\
  -F "medias=@/caminho/arquivo.jpg")
# 200 (conexão oficial) => { "status": "SUCCESS", "messageId": 4822,
#   "messageIds": [4822], "wid": "wamid.HBgMNTU4NTk5OTk5OTk5FQIAERgS" }
# messageIds traz um id por arquivo, na ordem de envio. messageId/wid podem
# vir null (aceita, sem registro consultável): então não há status a consultar.
# Com problema de conta na conexão vem também "warning". Conexão QR Code
# (Baileys): apenas { "status": "SUCCESS" }, sem messageId.
# Consulte o resultado de cada arquivo:
for ID in $(echo "$RESP" | jq -r '(.messageIds // [])[] | select(. != null)'); do
  curl -s "${baseUrl}/api/messages/$ID/status" \\
    -H "Authorization: Bearer SEU_TOKEN" \\
    -H "X-Origin: ERP Financeiro"
done`,
    node: `const axios = require("axios");
const FormData = require("form-data");
const fs = require("fs");

(async () => {
  const form = new FormData();
  form.append("number", "558599999999");
  form.append("body", "Legenda do arquivo");
  form.append("userId", "");
  form.append("queueId", "");
  form.append("sendSignature", "true"); // assinar a mensagem com o nome do atendente
  form.append("closeTicket", "false"); // "true" encerra o ticket após o envio
  form.append("medias", fs.createReadStream("/caminho/arquivo.jpg"));

  const { data } = await axios.post("${endpoint}", form, {
    headers: {
      Authorization: "Bearer SEU_TOKEN",
      "X-Origin": "ERP Financeiro",
      ...form.getHeaders(),
    },
  });

  // Conexão oficial: data = { status: "SUCCESS", messageId, messageIds, wid, warning? }.
  // messageIds traz um id por arquivo, na ordem de envio. Baileys: só status.
  // Consulte o resultado de cada arquivo com a rota de status:
  // Um id null (sem registro consultável) é ignorado.
  for (const id of data.messageIds || []) {
    if (!id) continue;
    const { data: situacao } = await axios.get(
      "${baseUrl}/api/messages/" + id + "/status",
      { headers: { Authorization: "Bearer SEU_TOKEN", "X-Origin": "ERP Financeiro" } }
    );
    console.log(id, situacao.status); // accepted | sent | delivered | read | failed
  }
})();`,
    ts: `// "arquivo" é um File vindo de um <input type="file">
const form = new FormData();
form.append("number", "558599999999");
form.append("body", "Legenda do arquivo");
form.append("userId", "");
form.append("queueId", "");
form.append("sendSignature", "true"); // assinar a mensagem com o nome do atendente
form.append("closeTicket", "false"); // "true" encerra o ticket após o envio
form.append("medias", arquivo);

const resposta = await fetch("${endpoint}", {
  method: "POST",
  headers: { Authorization: "Bearer SEU_TOKEN", "X-Origin": "ERP Financeiro" },
  body: form,
});

// Conexão oficial; na conexão QR Code (Baileys) vem só { status: "SUCCESS" }.
const dados: {
  status: "SUCCESS";
  messageId?: number | null; // primeiro arquivo; null = sem registro consultável
  messageIds?: Array<number | null>; // um id por arquivo, na ordem de envio
  wid?: string | null;
  warning?: { code: number | null; category: string; reason: string; action: string; link: string | null; reasonOriginal: string | null };
} = await resposta.json();

// Consulte o resultado do primeiro arquivo (messageId null = nada a consultar):
if (dados.messageId) {
  const consulta = await fetch("${baseUrl}/api/messages/" + dados.messageId + "/status", {
    headers: { Authorization: "Bearer SEU_TOKEN", "X-Origin": "ERP Financeiro" },
  });
  const situacao: { status: "accepted" | "sent" | "delivered" | "read" | "failed" } =
    await consulta.json();
}`,
    php: `<?php
$ch = curl_init("${endpoint}");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "Authorization: Bearer SEU_TOKEN",
    "X-Origin: ERP Financeiro",
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, [
    "number"        => "558599999999",
    "body"          => "Legenda do arquivo",
    "userId"        => "",
    "queueId"       => "",
    "sendSignature" => "true",  // assinar a mensagem com o nome do atendente
    "closeTicket"   => "false", // "true" encerra o ticket após o envio
    "medias"        => new CURLFile("/caminho/arquivo.jpg"),
]);
$resposta = json_decode(curl_exec($ch), true);
curl_close($ch);

// Conexão oficial: ["status" => "SUCCESS", "messageId" => ..., "messageIds" => [...],
// "wid" => ..., "warning" => ...]. messageIds: um id por arquivo, na ordem de envio.
// Conexão QR Code (Baileys): só ["status" => "SUCCESS"].
foreach ($resposta["messageIds"] ?? [] as $id) {
    if (empty($id)) {
        continue; // null: sem registro consultável
    }
    $ch = curl_init("${baseUrl}/api/messages/" . $id . "/status");
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_HTTPHEADER, [
        "Authorization: Bearer SEU_TOKEN",
        "X-Origin: ERP Financeiro",
    ]);
    $situacao = json_decode(curl_exec($ch), true);
    curl_close($ch);
    // $situacao["status"]: accepted | sent | delivered | read | failed
}`,
    python: `import requests

resposta = requests.post(
    "${endpoint}",
    data={
        "number": "558599999999",
        "body": "Legenda do arquivo",
        "userId": "",
        "queueId": "",
        "sendSignature": "true",  # assinar a mensagem com o nome do atendente
        "closeTicket": "false",  # "true" encerra o ticket após o envio
    },
    files={"medias": open("/caminho/arquivo.jpg", "rb")},
    headers={"Authorization": "Bearer SEU_TOKEN", "X-Origin": "ERP Financeiro"},
)
dados = resposta.json()

# Conexão oficial: {"status": "SUCCESS", "messageId", "messageIds", "wid", "warning"?}.
# messageIds: um id por arquivo, na ordem de envio. Baileys: só {"status": "SUCCESS"}.
for id_mensagem in dados.get("messageIds", []):
    if not id_mensagem:
        continue  # None: sem registro consultável
    situacao = requests.get(
        f"${baseUrl}/api/messages/{id_mensagem}/status",
        headers={"Authorization": "Bearer SEU_TOKEN", "X-Origin": "ERP Financeiro"},
    ).json()
    print(id_mensagem, situacao["status"])  # accepted | sent | delivered | read | failed`,
  };
  const exampleTabs = [
    { id: "fields", label: i18n.t("aeros.api.tabs.fields") },
    { id: "curl", label: "cURL" },
    { id: "node", label: "Node.js" },
    { id: "ts", label: "TypeScript" },
    { id: "php", label: "PHP" },
    { id: "python", label: "Python" },
  ];

  // Linhas de exemplo: comentários (# e //) ficam apagados; o resto sai na
  // cor padrão do painel.
  const renderExampleCode = (source) =>
    source.split("\n").map((line, idx) => {
      const ehComentario = /^\s*(#|\/\/)/.test(line);
      return (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          className={`${classes.codeLine} ${ehComentario ? classes.codeComment : ""}`}
        >
          {line === "" ? " " : line}
        </div>
      );
    });

  // `method`/`url` aparecem no cabeçalho do painel (os de SMS usam GET e
  // outra rota); `authLine` troca a descrição do Bearer; `response` é uma
  // linha opcional de resposta na aba "Campos"; sem `lines` (GET) o bloco
  // de body some.
  const renderCodePanel = ({
    method = "POST",
    url = getEndpoint(),
    contentType,
    bodyLabel,
    lines,
    copyText,
    examples,
    authLine = i18n.t("aeros.api.authorizationLine"),
    response,
    label,
  }) => {
    const activeCopy = exampleLang === "fields" ? copyText : examples[exampleLang];
    return (
      <div className={classes.codePanel}>
        <div className={classes.codeHeader}>
          <span className={classes.codeHeaderBadge}>{method}</span>
          <span className={classes.codeHeaderUrl} title={url}>
            {url}
          </span>
          <Tooltip title={i18n.t("aeros.api.copyExample")}>
            <IconButton
              size="small"
              className={classes.codeCopy}
              onClick={() => handleCopy(activeCopy)}
              aria-label={i18n.t("aeros.api.copyExample")}
            >
              <FileCopyOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <div
          className={classes.tabsRow}
          role="tablist"
          aria-label={label || undefined}
        >
          {exampleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={exampleLang === tab.id}
              className={`${classes.tab} ${
                exampleLang === tab.id ? classes.tabActive : ""
              }`}
              onClick={() => setExampleLang(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className={classes.codeBody}>
          {exampleLang === "fields" ? (
            <>
              <div className={`${classes.codeLine} ${classes.codeComment}`}>
                {"# Headers"}
              </div>
              <CodeLine
                classes={classes}
                indent={0}
                text={authLine}
              />
              <div className={`${classes.codeLine} ${classes.codeComment}`}>
                {`# ${i18n.t("messagesAPIInstructions.headerOrigin")}`}
              </div>
              {contentType && (
                <CodeLine classes={classes} indent={0} text={`Content-Type: ${contentType}`} />
              )}
              {lines && lines.length > 0 && (
                <>
                  <div className={classes.codeLine}>&nbsp;</div>
                  <div className={`${classes.codeLine} ${classes.codeComment}`}>
                    {`# ${bodyLabel}`}
                  </div>
                  <div className={classes.codeLine}>{"{"}</div>
                  {lines.map((line) => (
                    <CodeLine key={line} classes={classes} text={line} />
                  ))}
                  <div className={classes.codeLine}>{"}"}</div>
                </>
              )}
              {response && (
                <>
                  <div className={classes.codeLine}>&nbsp;</div>
                  <div className={`${classes.codeLine} ${classes.codeComment}`}>
                    {"# Response"}
                  </div>
                  <CodeLine classes={classes} indent={0} text={response} />
                </>
              )}
            </>
          ) : (
            renderExampleCode(examples[exampleLang])
          )}
        </div>
      </div>
    );
  };

  const renderLabel = (texto, { required, optional } = {}) => (
    <div className={classes.fieldLabel}>
      {texto}
      {required && <span className={classes.required}>*</span>}
      {optional && <span className={classes.optional}>{i18n.t("aeros.api.optional")}</span>}
    </div>
  );

  const statusDot = (color) => (
    <span className={classes.connDot} style={{ backgroundColor: color }} />
  );

  const renderConnectionSelect = (setFieldValue) => (
    <div>
      {renderLabel(i18n.t("aeros.api.connection"))}
      <TextField
        select
        value={selectedConnId}
        onChange={(e) => {
          const connId = e.target.value;
          setSelectedConnId(connId);
          const conn = connections.find((item) => item.id === connId);
          setFieldValue("token", conn?.token || "");
        }}
        variant="outlined"
        fullWidth
        className={classes.input}
        SelectProps={{
          displayEmpty: true,
          renderValue: (value) => {
            const conn = connections.find((item) => item.id === value);
            if (!conn) return i18n.t("aeros.api.connectionNone");
            const oficial = conn.channel === "whatsapp_oficial";
            return (
              <span className={classes.connOption}>
                {statusDot(conn.status === "CONNECTED" ? AEROS.success : AEROS.error)}
                {conn.name}
                {conn.number ? ` · ${conn.number}` : ""}
                <span
                  className={`${classes.channelTag} ${
                    oficial ? classes.channelTagOfficial : ""
                  }`}
                >
                  {oficial
                    ? i18n.t("aeros.api.channelOfficial")
                    : i18n.t("aeros.api.channelBaileys")}
                </span>
              </span>
            );
          },
        }}
      >
        <MenuItem value="">{i18n.t("aeros.api.connectionNone")}</MenuItem>
        {connections.map((conn) => (
          <MenuItem key={conn.id} value={conn.id}>
            {conn.name}
            {conn.channel === "whatsapp_oficial"
              ? ` (${i18n.t("aeros.api.channelOfficial")})`
              : ""}
          </MenuItem>
        ))}
      </TextField>
      <div className={classes.helper}>{i18n.t("aeros.api.connectionHelper")}</div>
    </div>
  );

  const renderTokenAndNumber = (prefixo) => (
    <div className={classes.fieldPair}>
      <div>
        {renderLabel(i18n.t("aeros.api.token"), { required: true })}
        <Field
          as={TextField}
          name="token"
          type={showToken ? "text" : "password"}
          variant="outlined"
          fullWidth
          required
          className={`${classes.input} ${classes.inputMono}`}
          inputProps={{ "aria-label": i18n.t("aeros.api.token") }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  className={classes.iconGhost}
                  onClick={() => setShowToken((atual) => !atual)}
                  aria-label={i18n.t(
                    showToken ? "aeros.api.tokens.hide" : "aeros.api.tokens.reveal"
                  )}
                >
                  {showToken ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
      <div>
        {renderLabel(i18n.t("aeros.api.number"), { required: true })}
        <Field
          as={TextField}
          name="number"
          placeholder="558599999999"
          variant="outlined"
          fullWidth
          required
          className={`${classes.input} ${classes.inputNumeric}`}
          inputProps={{ "aria-label": `${prefixo} ${i18n.t("aeros.api.number")}` }}
        />
      </div>
    </div>
  );

  const renderUserAndQueue = () => (
    <div className={classes.fieldPair}>
      <div>
        {renderLabel(i18n.t("aeros.api.userId"), { optional: true })}
        <Field
          as={TextField}
          name="userId"
          variant="outlined"
          fullWidth
          className={classes.input}
        />
      </div>
      <div>
        {renderLabel(i18n.t("aeros.api.queueId"), { optional: true })}
        <Field
          as={TextField}
          name="queueId"
          variant="outlined"
          fullWidth
          className={classes.input}
        />
      </div>
    </div>
  );

  const renderToggles = (values, setFieldValue) => (
    <div className={classes.toggleRow}>
      <FormControlLabel
        className={classes.toggleLabel}
        label={i18n.t("aeros.api.sendSignature")}
        control={
          <Switch
            classes={{ root: classes.switchRoot }}
            checked={!!values.sendSignature}
            onChange={(e) => setFieldValue("sendSignature", e.target.checked)}
          />
        }
      />
      <FormControlLabel
        className={classes.toggleLabel}
        label={i18n.t("aeros.api.closeTicket")}
        control={
          <Switch
            classes={{ root: classes.switchRoot }}
            checked={!!values.closeTicket}
            onChange={(e) => setFieldValue("closeTicket", e.target.checked)}
          />
        }
      />
    </div>
  );

  // "Último teste: 200 OK · há 4 min" — só aparece depois de um teste nesta
  // sessão; não há histórico persistido.
  const renderLastTest = () => {
    if (!lastTest) return null;
    const quando = tempoRelativo(lastTest.at);
    if (!quando) return null;
    const codigo = lastTest.status
      ? [lastTest.status, lastTest.statusText].filter(Boolean).join(" ")
      : i18n.t("aeros.api.result.noResponse");
    return (
      <span
        className={`${classes.lastTest} ${
          lastTest.ok ? classes.lastTestOk : classes.lastTestFail
        }`}
      >
        {lastTest.ok ? <CheckCircleIcon /> : <ErrorIcon />}
        {i18n.t("aeros.api.lastTest", { status: codigo, when: quando })}
      </span>
    );
  };

  const renderSubmit = (isSubmitting, label) => (
    <Button
      type="submit"
      variant="contained"
      className={classes.sendButton}
      disabled={isSubmitting}
      startIcon={isSubmitting ? undefined : <SendIcon />}
    >
      {isSubmitting ? <CircularProgress size={18} /> : label}
    </Button>
  );

  const renderFormMessageText = () => (
    <Formik
      initialValues={formMessageTextData}
      enableReinitialize={true}
      onSubmit={async (values, actions) => {
        const enviado = await handleSendTextMessage(values);
        actions.setSubmitting(false);
        // Só limpa o formulário quando deu certo: em erro a pessoa precisa do
        // que digitou para corrigir e reenviar.
        if (enviado) actions.resetForm();
      }}
    >
      {({ isSubmitting, setFieldValue, values }) => (
        <Form className={classes.card}>
          <div className={classes.formHead}>
            <div className={classes.formTitle}>{i18n.t("aeros.api.testSend")}</div>
            {ehOficial ? (
              <span className={classes.officialPill}>
                <VerifiedUserIcon />
                {i18n.t("aeros.api.channelOfficial")}
              </span>
            ) : (
              <span className={classes.formHint}>{i18n.t("aeros.api.testSendHint")}</span>
            )}
          </div>

          {renderConnectionSelect(setFieldValue)}
          {renderTokenAndNumber(i18n.t("aeros.api.methodText"))}

          {ehOficial && (
            <div>
              {renderLabel(i18n.t("aeros.api.template"))}
              <TextField
                select
                value={selectedTemplate?.shortcode || ""}
                onChange={(e) => handleSelectTemplate(e.target.value)}
                variant="outlined"
                fullWidth
                className={classes.input}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">{i18n.t("aeros.api.templateNone")}</MenuItem>
                {templates.map((tmpl) => (
                  <MenuItem key={tmpl.shortcode} value={tmpl.shortcode}>
                    {tmpl.shortcode} ({tmpl.variaveis}{" "}
                    {i18n.t(
                      tmpl.variaveis === 1
                        ? "aeros.api.variableOne"
                        : "aeros.api.variableMany"
                    )}
                    {tmpl.headerMidia ? `, ${i18n.t("aeros.api.templateMediaBadge")}` : ""}
                    )
                  </MenuItem>
                ))}
              </TextField>
              <div className={classes.helper}>{i18n.t("aeros.api.templateHelper")}</div>
            </div>
          )}

          {corpoIgnorado && (selectedTemplate.headerMidia || templateParams.length > 0) && (
            <div className={classes.templateBlock}>
              <div className={classes.templateBlockTitle}>
                {i18n.t("aeros.api.fillTemplate")}
              </div>
              {selectedTemplate.headerMidia && (
                <div>
                  {renderLabel(i18n.t("aeros.api.templateHeaderMediaUrl"), {
                    required: true,
                  })}
                  <TextField
                    value={templateHeaderMediaUrl}
                    onChange={(e) => setTemplateHeaderMediaUrl(e.target.value)}
                    placeholder="https://exemplo.com/arquivo.png"
                    variant="outlined"
                    fullWidth
                    required
                    className={`${classes.input} ${classes.inputMono}`}
                  />
                  <div className={classes.helper}>
                    {i18n.t("aeros.api.templateHeaderMediaUrlHelper")}
                  </div>
                </div>
              )}
              <div className={classes.fieldPair}>
                {(selectedTemplate.variaveisTokens || []).map((token, indice) => (
                  <div key={token}>
                    {renderLabel(`{{${token}}}`, { required: true })}
                    <TextField
                      value={templateParams[indice] || ""}
                      onChange={(e) => {
                        const novos = [...templateParams];
                        novos[indice] = e.target.value;
                        setTemplateParams(novos);
                      }}
                      variant="outlined"
                      fullWidth
                      required
                      className={classes.input}
                      inputProps={{ "aria-label": `{{${token}}}` }}
                    />
                    <div className={classes.helper}>
                      {i18n.t("aeros.api.templateParamHelper")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            {renderLabel(i18n.t("aeros.api.body"), { required: !corpoIgnorado })}
            {corpoIgnorado ? (
              <div className={classes.bodyDisabled}>
                <BlockIcon />
                {i18n.t("aeros.api.bodyIgnored")}
              </div>
            ) : (
              <Field
                as={TextField}
                name="body"
                variant="outlined"
                fullWidth
                multiline
                minRows={2}
                required
                className={classes.input}
                inputProps={{ "aria-label": i18n.t("aeros.api.body") }}
              />
            )}
          </div>

          {renderUserAndQueue()}
          {renderToggles(values, setFieldValue)}

          <div className={classes.formFooter}>
            {renderLastTest()}
            {renderSubmit(
              isSubmitting,
              i18n.t(corpoIgnorado ? "aeros.api.sendTemplate" : "aeros.api.sendTest")
            )}
          </div>
        </Form>
      )}
    </Formik>
  );

  const renderFileField = () => {
    const escolhido = file?.[0];
    const { Icon, tone } = fileVisual(escolhido?.type);
    const toneClass =
      tone === "image"
        ? classes.fileTileImage
        : tone === "other"
        ? classes.fileTileEmpty
        : "";
    return (
      <div>
        {renderLabel(i18n.t("aeros.api.file"), { required: true })}
        <div className={classes.fileCard}>
          <span
            className={`${classes.fileTile} ${
              escolhido ? toneClass : classes.fileTileEmpty
            }`}
          >
            {escolhido ? <Icon /> : <DescriptionOutlinedIcon />}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={classes.fileName}>
              {escolhido ? escolhido.name : i18n.t("aeros.api.noFile")}
            </div>
            <div className={classes.fileMeta}>
              {escolhido
                ? `${formatBytes(escolhido.size)} · ${
                    escolhido.type || i18n.t("aeros.api.unknownType")
                  }`
                : i18n.t("aeros.api.fileHint")}
            </div>
          </div>
          <Button component="label" className={classes.fileButton}>
            {i18n.t(escolhido ? "aeros.api.changeFile" : "aeros.api.chooseFile")}
            <input
              type="file"
              name="medias"
              id="medias"
              hidden
              onChange={(e) => setFile(e.target.files)}
            />
          </Button>
        </div>
      </div>
    );
  };

  const renderFormMessageMedia = () => (
    <Formik
      initialValues={formMessageMediaData}
      enableReinitialize={true}
      onSubmit={async (values, actions) => {
        const enviado = await handleSendMediaMessage(values);
        actions.setSubmitting(false);
        if (enviado) {
          actions.resetForm();
          setFile({});
          const input = document.getElementById("medias");
          if (input) input.value = null;
        }
      }}
    >
      {({ isSubmitting, setFieldValue, values }) => (
        <Form className={classes.card}>
          <div className={classes.formHead}>
            <div className={classes.formTitle}>{i18n.t("aeros.api.testSend")}</div>
          </div>

          {renderConnectionSelect(setFieldValue)}
          {renderTokenAndNumber(i18n.t("aeros.api.methodMedia"))}

          <div>
            {renderLabel(i18n.t("aeros.api.caption"), { optional: true })}
            <Field
              as={TextField}
              name="body"
              variant="outlined"
              fullWidth
              className={classes.input}
              inputProps={{ "aria-label": i18n.t("aeros.api.caption") }}
            />
          </div>

          {renderFileField()}
          {renderUserAndQueue()}
          {renderToggles(values, setFieldValue)}

          <div className={classes.formFooter}>
            {renderSubmit(isSubmitting, i18n.t("aeros.api.sendTest"))}
          </div>
        </Form>
      )}
    </Formik>
  );

  const responseCodes = [
    { code: "200", tone: AEROS.success, key: "200" },
    { code: "400", tone: AEROS.warning, key: "400" },
    { code: "401", tone: AEROS.error, key: "401" },
    { code: "402", tone: AEROS.error, key: "402" },
    { code: "404", tone: AEROS.warning, key: "404" },
    { code: "403", tone: AEROS.error, key: "403" },
    { code: "422", tone: AEROS.warning, key: "422" },
    { code: "429", tone: "#C08BFF", key: "429" },
  ];

  // Categorias de `failure.category` / `warning.category` (backend:
  // helpers/ErroMeta.ts). Textos em aeros.api.categories.<chave>.
  const failureCategories = [
    "conta",
    "contato",
    "janela",
    "template",
    "limite",
    "midia",
    "desconhecido",
  ];

  const renderKpis = () => {
    if (!usage) return null;
    const cards = [
      {
        value: Number(usage.total || 0).toLocaleString(),
        label: i18n.t("aeros.api.kpiSends", { days: usage.dias }),
        color: undefined,
        wide: true,
      },
      {
        value: Number(usage.texto || 0).toLocaleString(),
        label: i18n.t("aeros.api.kpiText"),
        color: AEROS.cyan,
      },
      {
        value: Number(usage.midia || 0).toLocaleString(),
        label: i18n.t("aeros.api.kpiMedia"),
        color: "#C08BFF",
      },
    ];
    return (
      <div className={classes.kpiGrid}>
        {cards.map((card) => (
          <div
            key={card.label}
            className={classes.kpiCard}
            style={card.wide ? { gridColumn: "1 / -1" } : undefined}
          >
            <div className={classes.kpiValue} style={card.color ? { color: card.color } : undefined}>
              {card.value}
            </div>
            <div className={classes.kpiLabel}>{card.label}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <MainContainer>
      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>{i18n.t("aeros.api.title")}</div>
          <div className={classes.pageSubtitle}>{i18n.t("aeros.api.subtitle")}</div>
        </div>
        <div className={c.headerActions}>
          <Button
            className={c.outlineButton}
            startIcon={<VpnKeyOutlinedIcon />}
            onClick={() => setTokensOpen(true)}
          >
            {i18n.t("aeros.api.tokensButton")}
          </Button>
        </div>
      </div>

      <div className={classes.body}>
        <div className={classes.introGrid}>
          <div className={classes.card}>
            <div className={classes.introText}>{i18n.t("aeros.api.intro")}</div>
            <div className={classes.endpointRow}>
              <span className={classes.methodBadge}>POST</span>
              <span className={classes.endpointText}>{getEndpoint()}</span>
              <Tooltip title={i18n.t("aeros.api.copyEndpoint")}>
                <IconButton
                  size="small"
                  className={classes.iconGhost}
                  onClick={() => handleCopy(getEndpoint())}
                  aria-label={i18n.t("aeros.api.copyEndpoint")}
                >
                  <FileCopyOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
            <div className={classes.numberRow}>
              {i18n.t("aeros.api.numberFormat")}
              <span className={classes.numberChips}>
                <span className={`${classes.numberChip} ${classes.chipCountry}`}>55</span>
                <span className={`${classes.numberChip} ${classes.chipArea}`}>85</span>
                <span className={`${classes.numberChip} ${classes.chipNumber}`}>
                  99999999
                </span>
              </span>
              <span className={classes.numberLegend}>
                {i18n.t("aeros.api.numberLegend")}
              </span>
            </div>
          </div>
          {renderKpis()}
        </div>

        <section className={classes.section}>
          <div>
            <div className={classes.eyebrow}>
              {i18n.t("aeros.api.method")} · 1/2
            </div>
            <div className={classes.sectionTitle}>{i18n.t("aeros.api.methodText")}</div>
            <div className={classes.sectionIntro}>
              {i18n.t("aeros.api.methodTextIntro")}
            </div>
          </div>
          <div className={classes.methodGrid}>
            {renderCodePanel({
              contentType: "application/json",
              bodyLabel: "Body (JSON)",
              lines: textBodyLines,
              response: i18n.t("messagesAPIInstructions.responseText"),
              copyText: textCopyExample,
              examples: textExamples,
            })}
            {renderFormMessageText()}
          </div>
        </section>

        <section className={classes.section}>
          <div>
            <div className={classes.eyebrow}>
              {i18n.t("aeros.api.method")} · 2/2
            </div>
            <div className={classes.sectionTitle}>{i18n.t("aeros.api.methodMedia")}</div>
            <div className={classes.sectionIntro}>
              {i18n.t("aeros.api.methodMediaIntro")}
            </div>
          </div>
          <div className={classes.methodGrid}>
            {renderCodePanel({
              contentType: "multipart/form-data",
              bodyLabel: "FormData",
              lines: mediaBodyLines,
              response: i18n.t("messagesAPIInstructions.responseMedia"),
              copyText: [
                `POST ${getEndpoint()}`,
                "Authorization: Bearer SEU_TOKEN",
                "X-Origin: ERP Financeiro",
                "Content-Type: multipart/form-data",
                "",
                ...mediaBodyLines,
              ].join("\n"),
              examples: mediaExamples,
            })}
            {renderFormMessageMedia()}
          </div>
        </section>

        <section className={classes.section}>
          <div>
            <div className={classes.eyebrow}>{i18n.t("aeros.api.statusEyebrow")}</div>
            <div className={classes.sectionTitle}>{i18n.t("aeros.api.statusTitle")}</div>
            <div className={classes.sectionIntro}>{i18n.t("aeros.api.statusIntro")}</div>
          </div>
          <div className={classes.smsGrid}>
            {renderCodePanel({
              method: statusExamples.status.method,
              url: statusExamples.status.url,
              label: i18n.t("aeros.api.statusLabel"),
              lines: statusExamples.status.lines,
              response: statusExamples.status.response,
              copyText: statusExamples.status.copy,
              examples: statusExamples.status.examples,
            })}
            <div className={classes.codesGrid}>
              {failureCategories.map((key) => (
                <div key={key} className={classes.codeCard}>
                  <span
                    className={classes.codeCardBadge}
                    style={{ color: AEROS.warning, backgroundColor: `${AEROS.warning}22` }}
                  >
                    {key}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className={classes.codeCardTitle}>
                      {i18n.t(`aeros.api.categories.${key}.title`)}
                    </div>
                    <div className={classes.codeCardDesc}>
                      {i18n.t(`aeros.api.categories.${key}.desc`)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={classes.section}>
          <div>
            <div className={classes.eyebrow}>{i18n.t("aeros.api.smsEyebrow")}</div>
            <div className={classes.sectionTitle}>{i18n.t("aeros.api.smsTitle")}</div>
            <div className={classes.sectionIntro}>{i18n.t("aeros.api.smsIntro")}</div>
          </div>
          <div className={classes.smsGrid}>
            {renderCodePanel({
              method: smsExamples.send.method,
              url: smsExamples.send.url,
              label: i18n.t("aeros.api.smsSendLabel"),
              contentType: "application/json",
              bodyLabel: "Body (JSON)",
              lines: smsExamples.send.lines,
              response: smsExamples.send.response,
              copyText: smsExamples.send.copy,
              examples: smsExamples.send.examples,
              authLine: i18n.t("aeros.api.smsAuthorizationLine"),
            })}
            {renderCodePanel({
              method: smsExamples.status.method,
              url: smsExamples.status.url,
              label: i18n.t("aeros.api.smsStatusLabel"),
              lines: smsExamples.status.lines,
              response: smsExamples.status.response,
              copyText: smsExamples.status.copy,
              examples: smsExamples.status.examples,
              authLine: i18n.t("aeros.api.smsAuthorizationLine"),
            })}
            {renderCodePanel({
              method: smsExamples.balance.method,
              url: smsExamples.balance.url,
              label: i18n.t("aeros.api.smsBalanceLabel"),
              lines: smsExamples.balance.lines,
              response: smsExamples.balance.response,
              copyText: smsExamples.balance.copy,
              examples: smsExamples.balance.examples,
              authLine: i18n.t("aeros.api.smsAuthorizationLine"),
            })}
          </div>
        </section>

        <section className={classes.section}>
          <div>
            <div className={classes.eyebrow}>{i18n.t("aeros.api.reference")}</div>
            <div className={classes.sectionTitle}>{i18n.t("aeros.api.responses")}</div>
          </div>
          <div className={classes.codesGrid}>
            {responseCodes.map((item) => (
              <div key={item.code} className={classes.codeCard}>
                <span
                  className={classes.codeCardBadge}
                  style={{ color: item.tone, backgroundColor: `${item.tone}22` }}
                >
                  {item.code}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className={classes.codeCardTitle}>
                    {i18n.t(`aeros.api.codes.${item.key}.title`)}
                  </div>
                  <div className={classes.codeCardDesc}>
                    {i18n.t(`aeros.api.codes.${item.key}.desc`)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <TokensModal
        open={tokensOpen}
        onClose={() => setTokensOpen(false)}
        connections={connections}
        onCopy={handleCopy}
        smsToken={smsToken}
        isAdmin={isAdmin}
        onGenerateSmsToken={handleGenerateSmsToken}
      />
      <ResultDrawer result={result} onClose={() => setResult(null)} onCopy={handleCopy} />
    </MainContainer>
  );
};

export default MessagesAPI;
